// FILECONTENT_START src/common/components/Form/Form.tsx
import { Skeleton } from "@mui/material";
import { FakeEvent } from "common/types";
import I18n from "i18n-js";
import { get } from "lodash";
import { cloneElement, ReactElement, ReactNode, useRef } from "react";
import { deepMap } from "react-children-utilities";

export interface IDictionary<TValue> {
  [id: string]: TValue;
}

export interface FormInputProps<T = any, O = any> {
  id?: string;
  error?: IDictionary<any>;
  onChange?: (event: FakeEvent<T>) => void;
  value?: T | null;
  options?: O;
  disabled?: boolean;
  requiredFields?: any;
}

export interface FormProps {
  children: ReactNode;
  item?: IDictionary<any>;
  errors?: IDictionary<any>;
  options?: IDictionary<any>;
  defaultItem?: IDictionary<any>;
  onChange: Function;
  onValidate?: (evt: any, item: any, field?: string | null) => boolean;
  translationCategory?: string;
  translationPlaceholdersCategory?: string;
  translationHelpersCategory?: string;
  idParam?: string;
  disabled?: boolean;
  isDirty?: (id?: string) => boolean;
  disabledFields?: Array<string>;
  loading?: boolean;
  isView?: boolean;
  requiredFields?: string[];
  getFieldProps?: (item: any, field: string) => any;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
} /* use `interface` if exporting so that consumers can extend */

export default function Form({
  children,
  item = {},
  errors = {},
  options = {},
  onChange,
  translationCategory,
  translationPlaceholdersCategory,
  translationHelpersCategory,
  idParam = "id",
  disabled,
  disabledFields = [],
  loading,
  isDirty: _isDirty,
  isView,
  requiredFields,
  validateOnBlur,
  validateOnChange,
  onValidate,
  getFieldProps,
}: FormProps) {
  const originalValues = useRef(item || ({} as IDictionary<any>));

  function reset(id: string) {
    let _item = Object.assign({}, item);
    delete _item[id];
    onChange && onChange(_item);
  }

  const handleChange =
    (id: string, originalEventHandler: Function) =>
    (evt: any, option: object) => {
      const value = evt.target.value;
      let _item = Object.assign({}, item);
      if (JSON.stringify(_item[id]) === JSON.stringify(value)) {
        return;
      }
      _item[id] = value;
      originalEventHandler && originalEventHandler(value);
      validateOnChange && onValidate && onValidate(evt, _item, id);
      onChange && onChange(_item, id, option);
    };
  const handleBlur = (
    evt: any,
    id: string | null,
    originalEventHandler: Function
  ) => {
    console.log("handleBlur");
    validateOnBlur && onValidate && onValidate(evt, item, id);
    originalEventHandler && originalEventHandler(evt);
  };

  function isDisabled(id: string) {
    return disabled || disabledFields.includes(id);
  }

  function isDirty(id: string) {
    return (
      JSON.stringify(originalValues.current[id]) !== JSON.stringify(item[id])
    );
  }

  return (
    <>
      {/*@ts-ignore*/}
      {deepMap(children, (child: ReactNode) => {
        const id = get(child, `props.${idParam}`);
        const roleProp = get(child, "props.role");
        const resetProp = get(child, "props.reset");
        const onUndefined = get(child, "props.onUndefined");
        const customOnChange = get(child, "props.customOnChange");
        if (id) {
          if (loading) {
            return (
              <Skeleton
                key={id}
                variant="rectangular"
                sx={{
                  borderRadius: 1,
                  flex: 1,
                  minWidth: "100%",
                  minHeight: "49px",
                }}
              >
                {child}
              </Skeleton>
            );
          }
          return cloneElement(child as ReactElement, {
            label:
              translationCategory &&
              I18n.t(`${translationCategory}.${id}`, {
                defaultValue: `${translationCategory}.${id}`,
              }),
            placeholder:
              translationPlaceholdersCategory && id !== "sort"
                ? I18n.t(`${translationPlaceholdersCategory}.${id}`, {
                    defaultValue: "",
                  })
                : undefined,
            helperText:
              translationHelpersCategory && id !== "sort"
                ? I18n.t(`${translationHelpersCategory}.${id}`, {
                    defaultValue: "",
                  })
                : undefined,
            value: item[id] === undefined ? onUndefined : item[id],
            error: errors ? errors[id] : undefined,
            options: options ? options[id] : undefined,
            isDirty: isDirty(id),
            disabled: isDisabled(id),
            required: requiredFields ? requiredFields.includes(id) : undefined,
            ...(getFieldProps ? getFieldProps(item, id) || {} : {}),
            ...(child as ReactElement).props,
            onChange:
              customOnChange ||
              handleChange(id, (child as ReactElement).props.onChange),
            onBlur:
              onValidate && validateOnBlur
                ? (evt: any) =>
                    handleBlur(evt, id, (child as ReactElement).props.onBlur)
                : (child as ReactElement).props.onBlur,
          });
        } else if (roleProp) {
          return cloneElement(child as ReactElement, {
            ...(child as ReactElement).props,
            ...(getFieldProps ? getFieldProps(item, roleProp) || {} : {}),
          });
        } else if (resetProp) {
          return cloneElement(child as ReactElement, {
            ...(child as ReactElement).props,
            reset: undefined,
            onReset: reset(resetProp),
          });
        }
        return child;
      })}
    </>
  );
}

// FILECONTENT_END src/common/components/Form/Form.tsx
